/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { formatValue, formatCompact } from "../../DataRoom/format";
import type { RevenueBridge as RevenueBridgeData } from "@app/api/trpc/routers/overview-charts";

/**
 * Gross-to-net waterfall.
 *
 * Reads top to bottom as a sentence: this is what we rang up, this is what we
 * gave away, this is what is actually ours, and this much of it belongs to the
 * tax office.
 *
 * COLOUR IS AN ANCHOR/DIVERGING JOB, NOT A CATEGORICAL ONE
 *   The five-hue CHART_PALETTE encodes *identity* — which product, which
 *   campaign. Here the bars encode *direction*: a total, a reduction, a result.
 *   Reusing categorical hues would say "these five things are different kinds
 *   of thing" when what matters is the sign. So: neutral ink for the anchors
 *   (gross, net), a single warm hue for every reduction, teal for the figure
 *   the operator keeps, and the VAT step muted because it is a pass-through
 *   rather than a business loss.
 *
 * RETURNS ARE NOT SUBTRACTED FROM gross_revenue
 *   The SQL's `gross_revenue` is already net of refunds and voids. Showing a
 *   "− Returns" bar hanging off it would subtract them a second time. When
 *   reversals exist we anchor one step higher instead — `gross_sales =
 *   gross_revenue + returns` — so the bar chain still lands on the true
 *   `gross_revenue`. With no reversals in the period the step is omitted
 *   entirely rather than drawn as a zero-height bar.
 */

type Step = {
  key: string;
  labelKey: string;
  /** Signed contribution. Positive grows the running total, negative shrinks it. */
  delta: number;
  /** Anchors are absolute positions, not deltas — they reset the running total. */
  anchor?: boolean;
  tone: "anchor" | "reduction" | "result" | "passthrough";
};

const TONE_CLASS: Record<Step["tone"], string> = {
  // Ink, not a hue: an anchor is "everything so far", not a category.
  anchor: "bg-base-content/70",
  // One warm hue for every giveaway, so reductions read as one family.
  reduction: "bg-[#e2622c]",
  // The figure the operator actually keeps.
  result: "bg-[#0f9488]",
  // VAT is collected on someone else's behalf, so it must not read as another
  // reduction — a distinct hue rather than a dimmer orange. Slate was tried
  // first and rejected by the validator: chroma 0.031 (reads gray) and only
  // ΔE 5.9 from the teal under deuteranopia. This step clears every check
  // against both the light and dark chart surfaces.
  passthrough: "bg-[#9d4edd]",
};

/**
 * Text steps are a different lightness from the fills they label — and a
 * different one per theme.
 *
 * A fill only has to clear 3:1 against the surface; a number is body text and
 * has to clear 4.5:1. One fixed hex cannot do that on both surfaces: the dark
 * step that passes on white (6.1–8.0:1) collapses to 2.0–2.6:1 on the dark
 * surface. So each tone carries a dark step for light mode and a light step for
 * dark mode, same hue in both. Measured against #ffffff and #1d232a; every
 * value below is >= 4.5:1 on its own surface.
 */
const TONE_TEXT: Record<Step["tone"], string> = {
  anchor: "text-base-content",
  reduction: "text-[#a8410f] dark:text-[#f08a53]",
  result: "text-[#066a61] dark:text-[#3fbfae]",
  passthrough: "text-[#7229ad] dark:text-[#c79bec]",
};

export type RevenueBridgeProps = {
  data: RevenueBridgeData | null;
  currency: string;
  loading?: boolean;
};

export const RevenueBridge = defineComponent<RevenueBridgeProps>({
  name: "RevenueBridge",
  props: ["data", "currency", "loading"] as any,
  setup(props) {
    const { t } = useLocalization();

    const steps = computed<Step[]>(() => {
      const d = props.data;
      if (!d) return [];
      const num = (v: unknown) => Number(v) || 0;

      const gross = num(d.gross_revenue);
      const returns = num(d.returns);
      const discounts = num(d.discounts);
      const promotions = num(d.promotions);
      const net = num(d.net_revenue);
      const vat = num(d.net_vat);
      const netExclVat = num(d.net_excl_vat);

      const out: Step[] = [];

      // Only surface the returns pair when reversals actually happened;
      // otherwise the anchor IS gross_revenue and an extra step is noise.
      if (Math.abs(returns) > 0.005) {
        out.push({
          key: "gross_sales",
          labelKey: "reports.overview.bridge.gross_sales",
          delta: gross + returns,
          anchor: true,
          tone: "anchor",
        });
        out.push({
          key: "returns",
          labelKey: "reports.overview.bridge.returns",
          delta: -returns,
          tone: "reduction",
        });
      }

      out.push({
        key: "gross_revenue",
        labelKey: "reports.overview.bridge.gross_revenue",
        delta: gross,
        anchor: true,
        tone: "anchor",
      });
      if (Math.abs(discounts) > 0.005) {
        out.push({
          key: "discounts",
          labelKey: "reports.overview.bridge.discounts",
          delta: -discounts,
          tone: "reduction",
        });
      }
      if (Math.abs(promotions) > 0.005) {
        out.push({
          key: "promotions",
          labelKey: "reports.overview.bridge.promotions",
          delta: -promotions,
          tone: "reduction",
        });
      }
      out.push({
        key: "net_revenue",
        labelKey: "reports.overview.bridge.net_revenue",
        delta: net,
        anchor: true,
        tone: "result",
      });
      out.push({
        key: "net_vat",
        labelKey: "reports.overview.bridge.net_vat",
        delta: -vat,
        tone: "passthrough",
      });
      out.push({
        key: "net_excl_vat",
        labelKey: "reports.overview.bridge.net_excl_vat",
        delta: netExclVat,
        anchor: true,
        tone: "result",
      });

      return out;
    });

    /** Running start/end for every bar, so floating segments land correctly. */
    const bars = computed(() => {
      let running = 0;
      return steps.value.map((s) => {
        const start = s.anchor ? 0 : running;
        const end = s.anchor ? s.delta : running + s.delta;
        running = end;
        return {
          step: s,
          start: Math.min(start, end),
          end: Math.max(start, end),
          value: s.anchor ? s.delta : s.delta,
          total: end,
        };
      });
    });

    const scaleMax = computed(() => {
      const m = Math.max(0, ...bars.value.map((b) => b.end));
      return m > 0 ? m : 1;
    });

    return () => {
      if (props.loading) {
        return (
          <div class="card transition-shadow border rounded-lg md:rounded-box">
            <div class="card-body p-4 md:p-6 flex h-[260px] items-center justify-center">
              <span class="loading loading-spinner loading-md text-primary/70" />
            </div>
          </div>
        );
      }

      if (!props.data || steps.value.length === 0) {
        return (
          <div class="card transition-shadow border rounded-lg md:rounded-box">
            <div class="card-body p-4 md:p-6">
              <h3 class="card-title flex items-center gap-2 text-accent font-normal">
                <span class="material-symbols-outlined">account_balance</span>
                {t("reports.overview.bridge.title")}
              </h3>
              <p class="mt-6 text-center text-sm text-base-content/60">
                {t("reports.overview.bridge.empty")}
              </p>
            </div>
          </div>
        );
      }

      return (
        <div class="card transition-shadow border rounded-lg md:rounded-box">
          <div class="card-body p-4 md:p-6">
            <header class="mb-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <h3 class="card-title flex items-center gap-2 text-accent font-normal">
                <span class="material-symbols-outlined">account_balance</span>
                {t("reports.overview.bridge.title")}
              </h3>
              <p class="mb-4 text-xs text-base-content/60">
                {t("reports.overview.bridge.description")}
              </p>
              <span class="text-xs text-base-content/60">
                {t("reports.overview.bridge.sales_count", [
                  props.data.sale_count ?? 0,
                ])}
              </span>
            </header>

            {/*
            Horizontal bars: the step labels are words, not dates, and reading
            them down the left edge beats rotating them under a vertical axis.
            This also removes the label-collision problem at 375px entirely.
          */}
            <ul class="flex flex-col gap-2.5">
              {bars.value.map((b) => {
                const leftPct = (b.start / scaleMax.value) * 100;
                const widthPct = ((b.end - b.start) / scaleMax.value) * 100;
                const signed = b.step.anchor ? b.total : b.step.delta;
                return (
                  <li key={b.step.key} class="flex flex-col gap-1">
                    <div class="flex items-baseline justify-between gap-2">
                      <span
                        class={[
                          "text-xs text-accent",
                          b.step.anchor ? " " : "",
                        ].join(" ")}
                      >
                        {t(b.step.labelKey)}
                      </span>
                      <span
                        class={[
                          " text-xs ",
                          b.step.anchor ? "" : "",
                          TONE_TEXT[b.step.tone],
                        ].join(" ")}
                      >
                        {signed < 0 ? "−" : ""}
                        {formatValue(Math.abs(signed), "money", props.currency)}
                      </span>
                    </div>
                    <div class="relative h-4 w-full overflow-hidden rounded bg-base-200">
                      <div
                        class={[
                          "absolute inset-y-0 rounded",
                          TONE_CLASS[b.step.tone],
                        ].join(" ")}
                        style={{
                          left: `${leftPct}%`,
                          // Hairline floor so a rounding-dust step stays visible
                          // rather than collapsing to an invisible 0px bar.
                          width: `${Math.max(widthPct, 0.6)}%`,
                        }}
                        title={`${t(b.step.labelKey)}: ${formatValue(signed, "money", props.currency)}`}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>

            <p class="mt-4 border-t border-base-300 pt-3 text-[11px] leading-relaxed text-base-content/55">
              {t("reports.overview.bridge.footnote")}
            </p>
          </div>
        </div>
      );
    };
  },
});
